import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// import { OutstandingAmountResponse } from '../types/payment';
import { paymentService } from '../services/paymentService';
import { PaymentsList } from './PaymentsList';
import { AddPaymentModal } from './AddPaymentModal';
import { formatCurrency } from '../utils/currency';

interface InvoicePaymentsProps {
  companyId: string;
  invoiceId: string;
  invoiceNumber: string;
  totalAmount: number;
  currency: string;
}

export const InvoicePayments: React.FC<InvoicePaymentsProps> = ({
  companyId,
  invoiceId,
  invoiceNumber,
  totalAmount,
  currency
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const queryClient = useQueryClient();

  // Загружаем платежи
  const { data: payments = [], isLoading: paymentsLoading } = useQuery({
    queryKey: ['payments', companyId, invoiceId],
    queryFn: () => paymentService.getInvoicePayments(companyId, invoiceId)
  });

  // Загружаем остаток к доплате
  const { data: outstandingData, isLoading: outstandingLoading } = useQuery({
    queryKey: ['outstanding', companyId, invoiceId],
    queryFn: () => paymentService.getOutstandingAmount(companyId, invoiceId)
  });

  // Мутация для подтверждения платежа
  const confirmPaymentMutation = useMutation({
    mutationFn: (paymentId: string) => paymentService.confirmPayment(companyId, invoiceId, paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments', companyId, invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['outstanding', companyId, invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices', companyId] });
    }
  });

  // Мутация для отмены платежа
  const cancelPaymentMutation = useMutation({
    mutationFn: (paymentId: string) => paymentService.cancelPayment(companyId, invoiceId, paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments', companyId, invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['outstanding', companyId, invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices', companyId] });
    }
  });

  const handleConfirmPayment = (paymentId: string) => {
    if (window.confirm('Подтвердить этот платеж?')) {
      confirmPaymentMutation.mutate(paymentId);
    }
  };

  const handleCancelPayment = (paymentId: string) => {
    if (window.confirm('Отменить этот платеж?')) {
      cancelPaymentMutation.mutate(paymentId);
    }
  };

  const handlePaymentCreated = () => {
    queryClient.invalidateQueries({ queryKey: ['payments', companyId, invoiceId] });
    queryClient.invalidateQueries({ queryKey: ['outstanding', companyId, invoiceId] });
    queryClient.invalidateQueries({ queryKey: ['invoices', companyId] });
  };

  if (paymentsLoading || outstandingLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const outstandingAmount = outstandingData?.outstandingAmount || 0;
  const totalPaid = totalAmount - outstandingAmount;

  return (
    <div className="space-y-6">
      {/* Сводка по платежам */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-4">Сводка по платежам</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Общая сумма</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatCurrency(totalAmount, currency)}
            </p>
          </div>
          
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-sm text-green-600">Оплачено</p>
            <p className="text-lg font-semibold text-green-900">
              {formatCurrency(totalPaid, currency)}
            </p>
          </div>
          
          <div className="text-center p-3 bg-orange-50 rounded-lg">
            <p className="text-sm text-orange-600">Остаток</p>
            <p className="text-lg font-semibold text-orange-900">
              {formatCurrency(outstandingAmount, currency)}
            </p>
          </div>
        </div>

        {/* Прогресс-бар */}
        <div className="mt-4">
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Прогресс оплаты</span>
            <span>{Math.round((totalPaid / totalAmount) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(totalPaid / totalAmount) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Кнопка добавления платежа */}
      {outstandingAmount > 0 && (
        <div className="flex justify-end">
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Добавить платеж
          </button>
        </div>
      )}

      {/* Список платежей */}
      <div>
        <h3 className="text-lg font-semibold mb-4">История платежей</h3>
        <PaymentsList
          payments={payments}
          onConfirmPayment={handleConfirmPayment}
          onCancelPayment={handleCancelPayment}
          currency={currency}
        />
      </div>

      {/* Модальное окно добавления платежа */}
      <AddPaymentModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handlePaymentCreated}
        companyId={companyId}
        invoiceId={invoiceId}
        invoiceNumber={invoiceNumber}
        outstandingAmount={outstandingAmount}
        currency={currency}
      />
    </div>
  );
};
